/**
 * Transliterator — reference-grade Russian (Cyrillic) → Latin engine.
 *
 * Powers /russian-transliteration. Nine transliteration standards run in
 * parallel; this module is the single source of truth for the letter tables,
 * the case-folding rules and the forward / reverse / diff algorithms.
 *
 * Design rules (why an etalon):
 *   - Pure module. No DOM, no telemetry, no window.__toast. The blade page is
 *     the only place that touches the UI. This keeps it node-testable via the
 *     vm-sandbox pattern (tests/js/transliterator.test.mjs).
 *   - Deterministic. Same input → identical output, always.
 *   - Tables verified against PRIMARY sources, never against other online
 *     converters (which are routinely wrong):
 *       mvd-864     — Приказ МВД России от 30.07.2020 №864, приложение.
 *                     Identical to ICAO Doc 9303 transliteration table.
 *       gost-52535  — ГОСТ Р 52535.1-2006 (machine-readable passports 2010–15).
 *       gost-779-b  — ГОСТ 7.79-2000 система Б (ISO 9, ASCII digraphs).
 *       gost-779-a  — ГОСТ 7.79-2000 система А (ISO 9:1995, diacritics).
 *       scholarly   — scholarly Slavic transliteration (≈ DIN 1460).
 *       bgn-pcgn    — BGN/PCGN 1947 agreement (rev. 2007).
 *       ala-lc      — ALA-LC Romanization Tables, Russian.
 *       swift       — simplified ASCII table used by banks for SWIFT/telex.
 *       url-slug    — web-friendly: readable ASCII, lowercase, hyphens.
 *
 * Case folding — the subtle part. Output is built per source letter with a
 * case attribute; a multi-char output is rendered FULL-UPPER only when the
 * neighbouring source letter is also uppercase, else TITLE-case. This yields
 * Щи→Shchi, ЩИ→SHCHI, Щ→Shch, Я→Ia (never IA).
 *
 * Reverse transliteration is best-effort: strict & lossless only for the two
 * ISO-9 standards (gost-779-a / gost-779-b); for the rest it returns a primary
 * candidate plus an explicit ambiguity list. It never throws.
 */
(function (global) {
    'use strict';

    /* ── Cyrillic letter sets ─────────────────────────────────────── */

    // 18 letters whose Latin form is identical across all 9 standards.
    var BASE = {
        'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'з': 'z',
        'и': 'i', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n', 'о': 'o',
        'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u', 'ф': 'f'
    };
    // The 15 letters that differ between standards: е ё ж й х ц ч ш щ ъ ы ь э ю я
    var ALL_LETTERS = 'абвгдеёжзийклмнопрстуфхцчшщъыьэюя'.split('');
    var VOWELS = { 'а': 1, 'е': 1, 'ё': 1, 'и': 1, 'о': 1, 'у': 1, 'ы': 1, 'э': 1, 'ю': 1, 'я': 1 };

    function mk(overrides) {
        var m = {};
        for (var k in BASE) { if (BASE.hasOwnProperty(k)) m[k] = BASE[k]; }
        for (var o in overrides) { if (overrides.hasOwnProperty(o)) m[o] = overrides[o]; }
        return m;
    }

    /* ── The 9 standards ──────────────────────────────────────────── */
    // Order is fixed — it drives the comparison-panel row order and the diff
    // column order. Labels / explanations live in the lang files, NOT here.

    var STANDARDS = [
        {
            id: 'mvd-864', default: true, asciiOnly: true, reversible: 'lossy',
            map: mk({ 'е': 'e', 'ё': 'e', 'ж': 'zh', 'й': 'i', 'х': 'kh', 'ц': 'ts',
                'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': 'ie', 'ы': 'y', 'ь': '',
                'э': 'e', 'ю': 'iu', 'я': 'ia' })
        },
        {
            id: 'gost-52535', asciiOnly: true, reversible: 'lossy',
            map: mk({ 'е': 'e', 'ё': 'e', 'ж': 'zh', 'й': 'i', 'х': 'kh', 'ц': 'tc',
                'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': '', 'ы': 'y', 'ь': '',
                'э': 'e', 'ю': 'iu', 'я': 'ia' })
        },
        {
            id: 'gost-779-b', asciiOnly: true, reversible: 'strict',
            map: mk({ 'е': 'e', 'ё': 'yo', 'ж': 'zh', 'й': 'j', 'х': 'x', 'ц': 'cz',
                'ч': 'ch', 'ш': 'sh', 'щ': 'shh', 'ъ': '"', 'ы': "y'", 'ь': "'",
                'э': "e'", 'ю': 'yu', 'я': 'ya' }),
            context: 'gost-b-cz'
        },
        {
            id: 'gost-779-a', asciiOnly: false, reversible: 'strict',
            map: mk({ 'е': 'e', 'ё': 'ë', 'ж': 'ž', 'й': 'j', 'х': 'h', 'ц': 'c',
                'ч': 'č', 'ш': 'š', 'щ': 'ŝ', 'ъ': 'ʺ', 'ы': 'y', 'ь': 'ʹ',
                'э': 'è', 'ю': 'û', 'я': 'â' })
        },
        {
            id: 'scholarly', asciiOnly: false, reversible: 'lossy',
            map: mk({ 'е': 'e', 'ё': 'ë', 'ж': 'ž', 'й': 'j', 'х': 'x', 'ц': 'c',
                'ч': 'č', 'ш': 'š', 'щ': 'šč', 'ъ': 'ʺ', 'ы': 'y', 'ь': 'ʹ',
                'э': 'è', 'ю': 'ju', 'я': 'ja' })
        },
        {
            id: 'bgn-pcgn', asciiOnly: false, reversible: 'lossy',
            map: mk({ 'е': 'e', 'ё': 'ë', 'ж': 'zh', 'й': 'y', 'х': 'kh', 'ц': 'ts',
                'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': 'ʺ', 'ы': 'y', 'ь': 'ʹ',
                'э': 'e', 'ю': 'yu', 'я': 'ya' }),
            context: 'bgn-ye'
        },
        {
            id: 'ala-lc', asciiOnly: false, reversible: 'lossy',
            // t͡s i͡u i͡a use U+0361 COMBINING DOUBLE INVERTED BREVE.
            map: mk({ 'е': 'e', 'ё': 'ë', 'ж': 'zh', 'й': 'ĭ', 'х': 'kh',
                'ц': 't͡s', 'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': 'ʺ',
                'ы': 'y', 'ь': 'ʹ', 'э': 'ė', 'ю': 'i͡u', 'я': 'i͡a' })
        },
        {
            id: 'swift', asciiOnly: true, reversible: 'lossy',
            map: mk({ 'е': 'e', 'ё': 'e', 'ж': 'j', 'й': 'i', 'х': 'kh', 'ц': 'c',
                'ч': 'ch', 'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
                'э': 'e', 'ю': 'iu', 'я': 'ia' })
        },
        {
            id: 'url-slug', asciiOnly: true, reversible: 'none',
            map: mk({ 'е': 'e', 'ё': 'e', 'ж': 'zh', 'й': 'y', 'х': 'h', 'ц': 'c',
                'ч': 'ch', 'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
                'э': 'e', 'ю': 'yu', 'я': 'ya' }),
            post: 'slug'
        }
    ];

    var STD_BY_ID = {};
    for (var si = 0; si < STANDARDS.length; si++) STD_BY_ID[STANDARDS[si].id] = STANDARDS[si];

    // "Naive" map an English reader would expect — used for surprising-spelling
    // detection against the passport result.
    var NAIVE = mk({ 'е': 'e', 'ё': 'yo', 'ж': 'zh', 'й': 'y', 'х': 'kh', 'ц': 'ts',
        'ч': 'ch', 'ш': 'sh', 'щ': 'shch', 'ъ': '', 'ы': 'y', 'ь': '',
        'э': 'e', 'ю': 'yu', 'я': 'ya' });

    /* ── Tokenisation & case folding ──────────────────────────────── */

    function isCyr(ch) {
        var l = ch.toLowerCase();
        return BASE.hasOwnProperty(l) || 'еёжйхцчшщъыьэюя'.indexOf(l) >= 0;
    }

    // Split input into tokens: { src, lower, isCyr, isUpper }.
    function tokenize(text) {
        var out = [];
        for (var i = 0; i < text.length; i++) {
            var ch = text[i];
            var cyr = isCyr(ch);
            var low = ch.toLowerCase();
            out.push({ src: ch, lower: low, isCyr: cyr, isUpper: cyr && ch !== low });
        }
        return out;
    }

    // Render a (lowercase) Latin output with the source letter's case.
    // Full-upper when a neighbouring source Cyrillic letter is also uppercase;
    // otherwise title-case (only the first character capitalised).
    function applyCase(latin, isUpper, prevUpper, nextUpper) {
        if (!isUpper || latin === '') return latin;
        var goUpper;
        if (nextUpper === true) goUpper = true;
        else if (nextUpper === null) goUpper = (prevUpper === true);
        else goUpper = false;
        if (goUpper) return latin.toUpperCase();
        // Title-case: capitalise the first letter character. Skip leading
        // combining marks just in case.
        return latin.charAt(0).toUpperCase() + latin.slice(1);
    }

    /* ── Context rules ────────────────────────────────────────────── */

    // Returns a lowercase Latin override for the token at index i, or null.
    function contextOverride(rule, tokens, i) {
        var t = tokens[i];
        if (rule === 'bgn-ye') {
            if (t.lower !== 'е' && t.lower !== 'ё') return null;
            // Initial, after a vowel, or after й/ъ/ь → prefix y.
            var prev = null;
            for (var p = i - 1; p >= 0; p--) {
                if (tokens[p].isCyr) { prev = tokens[p].lower; break; }
                break; // a non-Cyrillic char breaks the word
            }
            var atBoundary = (prev === null);
            var afterVowel = prev && VOWELS.hasOwnProperty(prev);
            var afterSemi = prev === 'й' || prev === 'ъ' || prev === 'ь';
            if (atBoundary || afterVowel || afterSemi) {
                return t.lower === 'е' ? 'ye' : 'yë';
            }
            return null;
        }
        if (rule === 'gost-b-cz') {
            if (t.lower !== 'ц') return null;
            // c before е ё и й ы э (letters whose Latin form starts e/i/y/j);
            // cz otherwise.
            var next = null;
            if (i + 1 < tokens.length && tokens[i + 1].isCyr) next = tokens[i + 1].lower;
            if (next && 'еёийыэ'.indexOf(next) >= 0) return 'c';
            return 'cz';
        }
        return null;
    }

    /* ── Forward transliteration ──────────────────────────────────── */

    function slugify(s) {
        return s.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    function transliterateTokens(tokens, std) {
        var pieces = [];        // {src, latin, isCyr}
        for (var i = 0; i < tokens.length; i++) {
            var t = tokens[i];
            if (!t.isCyr) { pieces.push({ src: t.src, latin: t.src, isCyr: false }); continue; }
            var low = std.context ? (contextOverride(std.context, tokens, i) || std.map[t.lower]) : std.map[t.lower];
            if (low == null) low = t.lower; // unknown Cyrillic letter — pass lowercase
            // neighbour case for the case-folder
            var nextUpper = null, prevUpper = null;
            if (i + 1 < tokens.length && tokens[i + 1].isCyr) nextUpper = tokens[i + 1].isUpper;
            if (i - 1 >= 0 && tokens[i - 1].isCyr) prevUpper = tokens[i - 1].isUpper;
            var latin = applyCase(low, t.isUpper, prevUpper, nextUpper);
            pieces.push({ src: t.src, latin: latin, isCyr: true });
        }
        return pieces;
    }

    // Detect counter-intuitive passport spellings (Юлия→Iuliia …).
    function surprisingNotes(text) {
        var notes = [];
        var words = text.match(/[а-яё]+/gi) || [];
        var seen = {};
        var mvd = STD_BY_ID['mvd-864'];
        for (var w = 0; w < words.length; w++) {
            var word = words[w];
            var key = word.toLowerCase();
            if (seen[key]) continue;
            seen[key] = 1;
            var toks = tokenize(word);
            var passport = transliterateTokens(toks, mvd).map(function (p) { return p.latin; }).join('');
            var naive = '';
            for (var c = 0; c < toks.length; c++) {
                var nl = toks[c].isCyr ? (NAIVE[toks[c].lower] != null ? NAIVE[toks[c].lower] : toks[c].lower) : toks[c].src;
                naive += applyCase(nl, toks[c].isUpper,
                    (c > 0 && toks[c - 1].isCyr) ? toks[c - 1].isUpper : null,
                    (c + 1 < toks.length && toks[c + 1].isCyr) ? toks[c + 1].isUpper : null);
            }
            if (passport.toLowerCase() !== naive.toLowerCase()) {
                notes.push({ type: 'surprising', word: word, latin: passport, naive: naive });
            }
        }
        return notes;
    }

    /**
     * transliterate(text, standardId)
     *   → { output, tokens:[{src,latin,isCyr}], notes:[] }
     * notes carry surprising-spelling hints (only for the passport standard).
     */
    function transliterate(text, standardId) {
        text = text == null ? '' : String(text);
        var std = STD_BY_ID[standardId];
        if (!std) throw new Error('transliterate: unknown standard "' + standardId + '"');
        var pieces = transliterateTokens(tokenize(text), std);
        var output = pieces.map(function (p) { return p.latin; }).join('');
        if (std.post === 'slug') output = slugify(output);
        var notes = std.id === 'mvd-864' ? surprisingNotes(text) : [];
        return { output: output, tokens: pieces, notes: notes };
    }

    // Run all 9 standards in one call.
    function transliterateAll(text) {
        var res = {};
        for (var i = 0; i < STANDARDS.length; i++) {
            res[STANDARDS[i].id] = transliterate(text, STANDARDS[i].id);
        }
        return res;
    }

    /**
     * diff(allResults) → array, one entry per source position that is a
     * Cyrillic letter: { src, variants:{id:latin}, differs }.
     * Token arrays are aligned by index (all standards see the same input).
     */
    function diff(allResults) {
        var ids = STANDARDS.map(function (s) { return s.id; });
        var ref = allResults[ids[0]];
        if (!ref) return [];
        var rows = [];
        for (var i = 0; i < ref.tokens.length; i++) {
            if (!ref.tokens[i].isCyr) continue;
            var variants = {}, distinct = {}, n = 0;
            for (var k = 0; k < ids.length; k++) {
                var tk = allResults[ids[k]].tokens[i];
                var v = tk ? tk.latin : '';
                variants[ids[k]] = v;
                var low = v.toLowerCase();
                if (!distinct.hasOwnProperty(low)) { distinct[low] = 1; n++; }
            }
            rows.push({ src: ref.tokens[i].src, variants: variants, differs: n > 1 });
        }
        return rows;
    }

    /* ── Reverse transliteration (Latin → Cyrillic) ───────────────── */

    // How "expected" a Cyrillic letter is when a Latin form is ambiguous —
    // lower rank wins as the primary candidate.
    var REV_RANK = { 'е': 1, 'э': 4, 'ё': 6, 'и': 1, 'й': 3, 'ъ': 7, 'ь': 5 };
    function rankOf(c) { return REV_RANK.hasOwnProperty(c) ? REV_RANK[c] : 2; }

    function buildReverseMap(std) {
        var rev = {};
        function add(latin, cyr) {
            if (!latin) return;
            var key = latin.toLowerCase();
            if (!rev[key]) rev[key] = [];
            if (rev[key].indexOf(cyr) < 0) rev[key].push(cyr);
        }
        for (var c in std.map) { if (std.map.hasOwnProperty(c)) add(std.map[c], c); }
        // context-produced forms
        if (std.context === 'bgn-ye') { add('ye', 'е'); add('yë', 'ё'); }
        if (std.context === 'gost-b-cz') { add('c', 'ц'); }
        for (var k in rev) {
            if (rev.hasOwnProperty(k)) {
                rev[k].sort(function (a, b) { return rankOf(a) - rankOf(b); });
            }
        }
        return rev;
    }

    function recase(cyr, sample) {
        if (!sample) return cyr;
        var firstUpper = sample[0] !== sample[0].toLowerCase();
        var allUpper = sample === sample.toUpperCase() && sample !== sample.toLowerCase();
        if (allUpper && sample.length > 1) return cyr.toUpperCase();
        return firstUpper ? cyr.toUpperCase() : cyr;
    }

    /**
     * reverse(latinText, standardId) → { candidates:[string], ambiguities:[] }
     * Greedy longest-match against the inverted table. Never throws.
     */
    function reverse(latinText, standardId) {
        latinText = latinText == null ? '' : String(latinText);
        var std = STD_BY_ID[standardId];
        if (!std) throw new Error('reverse: unknown standard "' + standardId + '"');
        var rev = buildReverseMap(std);
        var lengths = [];
        for (var key in rev) { if (rev.hasOwnProperty(key) && lengths.indexOf(key.length) < 0) lengths.push(key.length); }
        lengths.sort(function (a, b) { return b - a; });

        var out = '', ambiguities = [], i = 0;
        while (i < latinText.length) {
            var matched = false;
            for (var li = 0; li < lengths.length; li++) {
                var len = lengths[li];
                if (i + len > latinText.length) continue;
                var slice = latinText.substr(i, len);
                var opts = rev[slice.toLowerCase()];
                if (opts && opts.length) {
                    out += recase(opts[0], slice);
                    if (opts.length > 1) {
                        ambiguities.push({ at: out.length - 1, latin: slice, options: opts.slice() });
                    }
                    i += len;
                    matched = true;
                    break;
                }
            }
            if (!matched) { out += latinText[i]; i++; }
        }
        return { candidates: [out], ambiguities: ambiguities };
    }

    /**
     * reverseBest(latinText) → { candidate, standardId, ambiguities }
     * Reverse without knowing the source standard: reverse under every
     * standard and keep the most plausible Cyrillic reading. "Plausible" =
     * fewest Cyrillic letters (a standard whose digraphs consume the Latin
     * greedily — e.g. BGN's "yu"→ю — yields a shorter, real word; a standard
     * that can't, like the passport reversing "Yuliya", yields the bloated
     * "Ыулиыа"), then fewest hard/soft signs, then fewest ambiguities.
     */
    function reverseBest(latinText) {
        latinText = latinText == null ? '' : String(latinText);
        if (!latinText.trim()) return { candidate: '', standardId: null, ambiguities: [] };
        var best = null;
        for (var i = 0; i < STANDARDS.length; i++) {
            var id = STANDARDS[i].id;
            var r = reverse(latinText, id);
            var cand = r.candidates[0] || '';
            var cyr = (cand.match(/[а-яё]/gi) || []).length;
            var signs = (cand.match(/[ъь]/gi) || []).length;
            var score = cyr * 10 + signs * 3 + r.ambiguities.length;
            if (!best || score < best.score) {
                best = { score: score, candidate: cand, standardId: id, ambiguities: r.ambiguities };
            }
        }
        return { candidate: best.candidate, standardId: best.standardId, ambiguities: best.ambiguities };
    }

    /* ── Verified-name lookup ─────────────────────────────────────── */

    // The NAMES dataset is attached by public/js/ru-names-passport.js.
    // ё↔е fold: users routinely type Артем for Артём, Федор for Фёдор.
    function lookupName(text) {
        var NAMES = api.NAMES;
        if (!NAMES || !text) return null;
        var key = String(text).trim().toLowerCase().replace(/ё/g, 'е');
        if (NAMES.given && NAMES.given[key]) return { kind: 'given', latin: NAMES.given[key] };
        if (NAMES.patronymic && NAMES.patronymic[key]) return { kind: 'patronymic', latin: NAMES.patronymic[key] };
        if (NAMES.surname && NAMES.surname[key]) return { kind: 'surname', latin: NAMES.surname[key] };
        return null;
    }

    /* ── Export ───────────────────────────────────────────────────── */

    var api = {
        transliterate: transliterate,
        transliterateAll: transliterateAll,
        diff: diff,
        reverse: reverse,
        reverseBest: reverseBest,
        lookupName: lookupName,
        applyCase: applyCase,        // exported for unit tests
        slugify: slugify,
        STANDARDS: STANDARDS,
        ALL_LETTERS: ALL_LETTERS,
        NAMES: null,                 // populated by ru-names-passport.js
        version: '1.0.0'
    };

    global.Transliterator = api;
})(typeof window !== 'undefined' ? window : globalThis);
